import { pad2 } from './pad2.ts';
import { parseServerInstant } from './serverInstant.ts';
import type { DateTimePreference } from './dateTimePreference.ts';

/**
 * Turn a stored UTC instant into the string a viewer sees, per their own `DateTimePreference`.
 *
 * Every function here reads a `Date`'s *local* getters (`getDate`, `getHours`, ...), never the UTC
 * ones — the browser's own timezone conversion from the stored instant to a wall-clock value is not
 * this module's concern, only how that already-resolved local value is *printed* is. Static name
 * tables rather than `Intl.DateTimeFormat` so the output is exact and deterministic in tests
 * regardless of the runtime's ICU data or default locale.
 *
 * Every `iso` argument below goes through `parseServerInstant`, not a bare `new Date(iso)` — see that
 * module's own header for why: this app's server does not actually send `Z`-suffixed ISO 8601, it
 * sends Postgres's `timestamptz` text format, which only V8's non-standard, implementation-defined
 * `Date` parsing happened to accept. `parseServerInstant` also accepts a genuine `.toISOString()`
 * string, so the two call sites in this file that build one locally (`SchedulePage`'s week-boundary
 * dates, via `weekRange.ts`) still work unchanged.
 */

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/** `MM/DD/YYYY`-shaped, per `preference.dateOrder`/`preference.separator`. */
export function formatDate(iso: string, preference: DateTimePreference): string {
  const date = parseServerInstant(iso);
  const day = pad2(date.getDate());
  const month = pad2(date.getMonth() + 1);
  const year = String(date.getFullYear());

  const parts =
    preference.dateOrder === 'MDY'
      ? [month, day, year]
      : preference.dateOrder === 'DMY'
        ? [day, month, year]
        : [year, month, day];
  return parts.join(preference.separator);
}

/**
 * `MM/DD/YYYY`-shaped, per `preference.dateOrder`/`preference.separator` — the calendar-date-only
 * counterpart to `formatDate` above, for a value with no time-of-day or timezone component at all
 * (`shared/src/clinicalRequests.ts`'s `calendarDateSchema`: a problem's `onsetDate`, a medication's
 * `startDate`/`endDate`, an immunization's `administeredDate`, a patient's `dateOfBirth`). Takes the
 * bare `YYYY-MM-DD` string directly rather than routing through `parseServerInstant`, which requires
 * a time component and throws on a date-only value — parsing the Y/M/D digits directly, with no
 * `Date` construction at all, is also what avoids the day-shift a UTC-midnight parse plus a
 * local-getter read would risk for a viewer west of UTC. Falls back to the raw string on anything
 * that doesn't match, matching `web/src/lib/dateFormat.ts`'s own `formatCalendarDate` fallback
 * behaviour (a distinct, older, non-preference-aware function of the same name in that file — see
 * its own header for why it predates this one and has not yet been migrated to it).
 */
export function formatPreferredCalendarDate(
  isoDate: string,
  preference: DateTimePreference,
): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (match === null) return isoDate;
  // Every capture group is required by the regex, so all three are always defined when `match` is
  // non-null — the `?? ''` fallbacks exist only to satisfy `noUncheckedIndexedAccess`'s
  // `string | undefined` inference over a regex match array, matching `serverInstant.ts`'s own
  // identical comment on the identical pattern.
  const [, year = '', month = '', day = ''] = match;
  const parts =
    preference.dateOrder === 'MDY'
      ? [month, day, year]
      : preference.dateOrder === 'DMY'
        ? [day, month, year]
        : [year, month, day];
  return parts.join(preference.separator);
}

/** `9:05 AM` or `09:05`, per `preference.hourCycle`. */
export function formatTime(iso: string, preference: DateTimePreference): string {
  const date = parseServerInstant(iso);
  const minutes = pad2(date.getMinutes());

  if (preference.hourCycle === 'h24') {
    return `${pad2(date.getHours())}:${minutes}`;
  }

  const rawHours = date.getHours();
  const suffix = rawHours >= 12 ? 'PM' : 'AM';
  const twelveHour = rawHours % 12 === 0 ? 12 : rawHours % 12;
  return `${String(twelveHour)}:${minutes} ${suffix}`;
}

/** `9 AM` / `2 PM` or `09:00` / `14:00` — an hour-boundary label for `CalendarGrid`'s time axis, per
 * `preference.hourCycle`. Takes a plain local hour (0-23), not a server instant — the calendar grid
 * already knows which local hour each gridline sits at from its own range computation, so this is a
 * pure formatting step, not a second parse. */
export function formatHour(hour: number, preference: DateTimePreference): string {
  if (preference.hourCycle === 'h24') {
    return `${pad2(hour)}:00`;
  }
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const twelveHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${String(twelveHour)} ${suffix}`;
}

/** `9:00 AM – 9:30 AM` — what an appointment row shows for its time slot. */
export function formatTimeRange(
  startsAt: string,
  endsAt: string,
  preference: DateTimePreference,
): string {
  return `${formatTime(startsAt, preference)} – ${formatTime(endsAt, preference)}`;
}

/** `Monday · 08/18/2026` — a day-group heading for the schedule list. */
export function formatDateHeading(iso: string, preference: DateTimePreference): string {
  const weekday = WEEKDAY_NAMES[parseServerInstant(iso).getDay()];
  return `${weekday} · ${formatDate(iso, preference)}`;
}

/** `08/18/2026, 3:05 PM` — a combined date+time cell, e.g. an audit-log timestamp column
 * (`routes/audit/LoginAuditLog.tsx`). Composed from `formatDate`/`formatTime` rather than a third
 * independent parse, matching every other function in this file. */
export function formatDateAndTime(iso: string, preference: DateTimePreference): string {
  return `${formatDate(iso, preference)}, ${formatTime(iso, preference)}`;
}

/** The local calendar day, as a sortable/comparable key (`YYYY-MM-DD`) — used to group a list of
 * appointments by day regardless of the viewer's own display-format preference. */
export function localDateKey(iso: string): string {
  const date = parseServerInstant(iso);
  return `${String(date.getFullYear())}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** Minutes since local midnight, as a real number (fractional past the minute) rather than an
 * integer — `CalendarGrid`'s own vertical positioning of an appointment block within its time axis,
 * kept here rather than inline in that component so the same wire-format-vs-`.toISOString()`
 * robustness `parseServerInstant` gives every other function in this file applies to it too. */
export function localMinutesSinceMidnight(iso: string): number {
  const date = parseServerInstant(iso);
  return date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;
}
